import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Edit2, Trash2, Check, X, RefreshCw, Key, Code } from 'lucide-react';
import TrackingScriptsManager from '../components/TrackingScriptsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'lead_source', label: 'Lead Source' },
  { value: 'calling', label: 'Calling System' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'analytics', label: 'Analytics' }
];

const PROVIDERS = {
  lead_source: ['Google Ads', 'Meta Ads', 'Justdial', 'Missed Call Service'],
  calling: ['FreJun', 'Twilio', 'Exotel'],
  sms: ['Twilio', 'MSG91', 'Gupshup'],
  whatsapp: ['Twilio', 'Gupshup', 'WhatsApp Business API'],
  analytics: ['Google Analytics', 'Mixpanel']
};

export default function IntegrationsManager() {
  const { getAuthHeader } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [missedCallNumbers, setMissedCallNumbers] = useState([]);
  const [activeTab, setActiveTab] = useState('integrations'); // 'integrations', 'api-keys', 'missed-call-numbers', or 'email-accounts'
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [showEmailAccountModal, setShowEmailAccountModal] = useState(false);
  const [editingEmailAccount, setEditingEmailAccount] = useState(null);
  const [testingEmail, setTestingEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showMissedCallModal, setShowMissedCallModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [editingMissedCallNumber, setEditingMissedCallNumber] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'lead_source',
    provider: '',
    api_key: '',
    base_url: '',
    additional_config: {}
  });

  const [apiKeyFormData, setApiKeyFormData] = useState({
    name: '',
    permissions: [],
    ip_whitelist: [],
    default_source: ''
  });

  const [sources, setSources] = useState([]);

  const [missedCallFormData, setMissedCallFormData] = useState({
    number: '',
    source_name: 'google',
    platform: 'Exotel',
    description: ''
  });

  const AVAILABLE_PERMISSIONS = [
    'create_lead',
    'update_lead',
    'view_lead',
    'create_consultation',
    'webhook_access'
  ];

  const MISSED_CALL_PLATFORMS = ['Exotel', 'Knowlarity', 'Ozonetel', 'Twilio', 'Other'];
  
  const SOURCE_OPTIONS = ['google', 'website', 'facebook', 'justdial', 'instagram', 'linkedin', 'other'];

  useEffect(() => {
    if (activeTab === 'integrations') {
      fetchIntegrations();
    } else if (activeTab === 'api-keys') {
      fetchApiKeys();
      fetchSources();
    } else if (activeTab === 'missed-call-numbers') {
      fetchMissedCallNumbers();
    }
  }, [activeTab]);

  const fetchSources = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/sources/`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setSources(response.data || []);
      console.log('Sources fetched:', response.data.length);
    } catch (error) {
      console.error('Error fetching sources:', error);
      toast({
        title: "Error",
        description: "Failed to fetch sources",
        variant: "destructive"
      });
    }
  };

  const fetchMissedCallNumbers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/missed-call-numbers`,
        { headers: getAuthHeader() }
      );
      setMissedCallNumbers(response.data);
    } catch (error) {
      console.error('Error fetching missed call numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/api-keys`,
        { headers: getAuthHeader() }
      );
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/integrations`,
        { headers: getAuthHeader() }
      );
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingIntegration) {
        await axios.put(
          `${BACKEND_URL}/api/admin/integrations/${editingIntegration.id}`,
          formData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/integrations`,
          formData,
          { headers: getAuthHeader() }
        );
      }
      
      setShowModal(false);
      setEditingIntegration(null);
      setFormData({
        name: '',
        category: 'lead_source',
        provider: '',
        api_key: '',
        base_url: '',
        additional_config: {}
      });
      fetchIntegrations();
    } catch (error) {
      console.error('Error saving integration:', error);
      alert('Failed to save integration: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (integration) => {
    setEditingIntegration(integration);
    setFormData({
      name: integration.name,
      category: integration.category,
      provider: integration.provider,
      api_key: integration.api_key,
      base_url: integration.base_url || '',
      additional_config: integration.additional_config || {}
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/integrations/${id}`,
        { headers: getAuthHeader() }
      );
      fetchIntegrations();
    } catch (error) {
      console.error('Error deleting integration:', error);
      alert('Failed to delete integration');
    }
  };

  const handleToggle = async (integration) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/integrations/${integration.id}`,
        { enabled: !integration.enabled },
        { headers: getAuthHeader() }
      );
      fetchIntegrations();
    } catch (error) {
      console.error('Error toggling integration:', error);
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/integrations/${id}/test`,
        {},
        { headers: getAuthHeader() }
      );
      
      alert(`Test Result: ${response.data.status}\n${response.data.message}`);
      fetchIntegrations();
    } catch (error) {
      console.error('Error testing integration:', error);
      alert('Test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestingId(null);
    }
  };

  // API Key Management Functions
  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/api-keys`,
        apiKeyFormData,
        { headers: getAuthHeader() }
      );
      
      setShowApiKeyModal(false);
      setApiKeyFormData({
        name: '',
        permissions: [],
        ip_whitelist: []
      });
      fetchApiKeys();
      alert('API Key created successfully! Make sure to copy it now - it won\'t be shown again.');
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Failed to create API key: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleApiKey = async (keyId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/api-keys/${keyId}/toggle`,
        {},
        { headers: getAuthHeader() }
      );
      fetchApiKeys();
    } catch (error) {
      console.error('Error toggling API key:', error);
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/api-keys/${keyId}`,
        { headers: getAuthHeader() }
      );
      fetchApiKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Failed to delete API key');
    }
  };

  const handleRegenerateApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to regenerate this API key? The old key will stop working immediately.')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/api-keys/${keyId}/regenerate`,
        {},
        { headers: getAuthHeader() }
      );
      fetchApiKeys();
      alert('API Key regenerated successfully! Make sure to update any systems using the old key.');
    } catch (error) {
      console.error('Error regenerating API key:', error);
      alert('Failed to regenerate API key');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(label);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Missed Call Numbers Management Functions
  const handleCreateMissedCallNumber = async (e) => {
    e.preventDefault();
    
    try {
      if (editingMissedCallNumber) {
        // Update existing
        await axios.put(
          `${BACKEND_URL}/api/admin/missed-call-numbers/${editingMissedCallNumber.id}`,
          missedCallFormData,
          { headers: getAuthHeader() }
        );
        alert('Missed call number updated successfully!');
      } else {
        // Create new
        await axios.post(
          `${BACKEND_URL}/api/admin/missed-call-numbers`,
          missedCallFormData,
          { headers: getAuthHeader() }
        );
        alert('Missed call number added successfully!');
      }
      
      setShowMissedCallModal(false);
      setEditingMissedCallNumber(null);
      setMissedCallFormData({
        number: '',
        source_name: 'google',
        platform: 'Exotel',
        description: ''
      });
      fetchMissedCallNumbers();
    } catch (error) {
      console.error('Error saving missed call number:', error);
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditMissedCallNumber = (number) => {
    setEditingMissedCallNumber(number);
    setMissedCallFormData({
      number: number.number,
      source_name: number.source_name,
      platform: number.platform,
      description: number.description || ''
    });
    setShowMissedCallModal(true);
  };

  const handleDeleteMissedCallNumber = async (numberId) => {
    if (!window.confirm('Are you sure you want to delete this missed call number?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/missed-call-numbers/${numberId}`,
        { headers: getAuthHeader() }
      );
      fetchMissedCallNumbers();
      alert('Missed call number deleted successfully!');
    } catch (error) {
      console.error('Error deleting missed call number:', error);
      alert('Failed to delete missed call number');
    }
  };

  const groupedIntegrations = integrations.reduce((acc, integration) => {
    if (!acc[integration.category]) {
      acc[integration.category] = [];
    }
    acc[integration.category].push(integration);
    return acc;
  }, {});

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Integrations</h1>
          <p className="body-medium">Manage third-party API integrations and internal API keys</p>
        </div>
        {activeTab !== 'tracking-pixels' && (
          <button
            className="btn-primary"
            onClick={() => {
              if (activeTab === 'integrations') {
                setEditingIntegration(null);
                setFormData({
                  name: '',
                  category: 'lead_source',
                  provider: '',
                  api_key: '',
                  base_url: '',
                  additional_config: {}
                });
                setShowModal(true);
              } else if (activeTab === 'api-keys') {
                setApiKeyFormData({
                  name: '',
                  permissions: [],
                  ip_whitelist: []
                });
                setShowApiKeyModal(true);
              } else if (activeTab === 'missed-call-numbers') {
                setEditingMissedCallNumber(null);
                setMissedCallFormData({
                  number: '',
                  source_name: 'google',
                  platform: 'Exotel',
                  description: ''
                });
                setShowMissedCallModal(true);
              }
            }}
          >
            <Plus size={20} />
            {activeTab === 'integrations' ? 'Add Integration' : activeTab === 'api-keys' ? 'Generate API Key' : 'Add Number'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '2px solid #e5e7eb', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          <button
            onClick={() => setActiveTab('integrations')}
            style={{
              padding: '12px 0',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'integrations' ? '2px solid #A08EA8' : '2px solid transparent',
              color: activeTab === 'integrations' ? '#A08EA8' : '#6b7280',
              fontWeight: activeTab === 'integrations' ? '600' : '400',
              fontSize: '16px',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            Third-Party Integrations
          </button>
          <button
            onClick={() => setActiveTab('api-keys')}
            style={{
              padding: '12px 0',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'api-keys' ? '2px solid #A08EA8' : '2px solid transparent',
              color: activeTab === 'api-keys' ? '#A08EA8' : '#6b7280',
              fontWeight: activeTab === 'api-keys' ? '600' : '400',
              fontSize: '16px',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            API Keys & Webhooks
          </button>
          <button
            onClick={() => setActiveTab('missed-call-numbers')}
            style={{
              padding: '12px 0',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'missed-call-numbers' ? '2px solid #A08EA8' : '2px solid transparent',
              color: activeTab === 'missed-call-numbers' ? '#A08EA8' : '#6b7280',
              fontWeight: activeTab === 'missed-call-numbers' ? '600' : '400',
              fontSize: '16px',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            Missed Call Numbers
          </button>
          <button
            onClick={() => setActiveTab('email-accounts')}
            style={{
              padding: '12px 0',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'email-accounts' ? '2px solid #A08EA8' : '2px solid transparent',
              color: activeTab === 'email-accounts' ? '#A08EA8' : '#6b7280',
              fontWeight: activeTab === 'email-accounts' ? '600' : '400',
              fontSize: '16px',
              cursor: 'pointer',
              marginBottom: '-2px'
            }}
          >
            📧 Email Accounts
          </button>
          <button
            onClick={() => setActiveTab('tracking-pixels')}
            style={{
              padding: '12px 0',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'tracking-pixels' ? '2px solid #A08EA8' : '2px solid transparent',
              color: activeTab === 'tracking-pixels' ? '#A08EA8' : '#6b7280',
              fontWeight: activeTab === 'tracking-pixels' ? '600' : '400',
              fontSize: '16px',
              cursor: 'pointer',
              marginBottom: '-2px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Code size={16} />
            Tracking Pixels
          </button>
        </div>
      </div>

      {/* Third-Party Integrations Tab */}
      {activeTab === 'integrations' && (
        <div>
          {loading ? (
            <div className="loading">Loading integrations...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              {CATEGORIES.map(category => (
            <div key={category.value}>
              <h2 className="heading-3" style={{ marginBottom: '16px' }}>
                {category.label}
              </h2>
              
              {groupedIntegrations[category.value]?.length > 0 ? (
                <div className="integrations-grid">
                  {groupedIntegrations[category.value].map(integration => (
                    <div key={integration.id} className="integration-card">
                      <div className="integration-header">
                        <div>
                          <h3 className="heading-4">{integration.name}</h3>
                          <p className="caption">{integration.provider}</p>
                        </div>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={integration.enabled}
                            onChange={() => handleToggle(integration)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      
                      <div className="integration-details">
                        <p className="body-small">
                          <strong>API Key:</strong> {integration.api_key.substring(0, 20)}...
                        </p>
                        {integration.base_url && (
                          <p className="body-small">
                            <strong>Base URL:</strong> {integration.base_url}
                          </p>
                        )}
                        {integration.last_tested && (
                          <p className="body-small">
                            <strong>Last Tested:</strong> {new Date(integration.last_tested).toLocaleDateString()}
                            <span className={`test-status ${integration.test_status}`}>
                              {integration.test_status}
                            </span>
                          </p>
                        )}
                      </div>

                      <div className="integration-actions">
                        <button
                          className="btn-secondary"
                          onClick={() => handleTest(integration.id)}
                          disabled={testingId === integration.id}
                        >
                          <RefreshCw size={16} className={testingId === integration.id ? 'spinning' : ''} />
                          {testingId === integration.id ? 'Testing...' : 'Test'}
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleEdit(integration)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => handleDelete(integration.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No integrations configured for {category.label}</p>
                </div>
              )}
            </div>
          ))}
            </div>
          )}
        </div>
      )}

      {/* API Keys & Webhooks Tab */}
      {activeTab === 'api-keys' && (
        <div>
          {/* API Documentation Banner */}
          <div style={{ 
            background: '#f0fdf4', 
            border: '1px solid #86efac', 
            borderRadius: '8px', 
            padding: '16px', 
            marginBottom: '24px' 
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#166534' }}>
              🔑 API for Lead Creation
            </h4>
            <div style={{ fontSize: '13px', color: '#166534', lineHeight: '1.6' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Endpoint:</strong> <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: '3px' }}>POST {BACKEND_URL}/api/leads</code>
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Authentication:</strong> Include your API key in the header: <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: '3px' }}>X-API-Key: your_api_key</code>
              </p>
              
              <p style={{ margin: '12px 0 8px 0', fontWeight: '600' }}>Required Fields:</p>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
                <li><code>name</code> - Lead's full name (string)</li>
                <li><code>email</code> - Valid email address (string)</li>
                <li><code>phone</code> - Phone number with country code (string)</li>
                <li><code>source</code> - Lead source: website, google_ads, facebook, instagram, referral, walk_in, phone_call, chatbot, whatsapp, justdial, event, other</li>
              </ul>
              
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Optional Fields:</p>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
                <li><code>service_type</code> - Service interested in (string)</li>
                <li><code>destination_country</code> - Destination country (string)</li>
                <li><code>description</code> - Additional details (string)</li>
                <li><code>temperature</code> - Lead temperature: hot, warm, cold (default: warm)</li>
                <li><code>priority</code> - Priority: low, medium, high, urgent (default: medium)</li>
                <li><code>tags</code> - Array of tags (array of strings)</li>
                <li><code>budget_indicated</code> - Budget amount (number)</li>
              </ul>

              <details style={{ marginTop: '12px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: '600', marginBottom: '8px' }}>📋 View Example Payload</summary>
                <pre style={{ 
                  background: '#fff', 
                  padding: '12px', 
                  borderRadius: '4px', 
                  border: '1px solid #86efac',
                  fontSize: '12px',
                  overflow: 'auto',
                  margin: '8px 0 0 0'
                }}>
{`{
  "name": "John Doe",
  "email": "john.doe@example.com",
  "phone": "+919876543210",
  "source": "website",
  "service_type": "Study Visa - Canada",
  "destination_country": "Canada",
  "description": "Interested in student visa for Canada",
  "temperature": "hot",
  "priority": "high",
  "tags": ["student", "canada"],
  "budget_indicated": 50000
}`}
                </pre>
              </details>
            </div>
          </div>

          {loading ? (
            <p>Loading API keys...</p>
          ) : apiKeys.length === 0 ? (
            <div className="empty-state">
              <Key size={48} style={{ margin: '0 auto 16px', display: 'block', opacity: 0.5 }} />
              <p>No API keys generated yet. Create your first API key to allow third-party systems to access your CRM.</p>
            </div>
          ) : (
            <div className="integrations-grid">
              {apiKeys.map(apiKey => (
                <div key={apiKey.id} className="integration-card">
                  <div className="integration-header">
                    <div>
                      <h3 className="heading-4">{apiKey.name}</h3>
                      <p className="caption">Created by {apiKey.created_by} on {new Date(apiKey.created_at).toLocaleDateString()}</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={apiKey.enabled}
                        onChange={() => handleToggleApiKey(apiKey.id)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  
                  <div className="integration-details">
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '13px' }}>API Key:</strong>
                        <button
                          onClick={() => copyToClipboard(apiKey.key, `key-${apiKey.id}`)}
                          style={{ 
                            padding: '4px 8px', 
                            fontSize: '11px', 
                            background: copiedKey === `key-${apiKey.id}` ? '#10b981' : '#f3f4f6',
                            color: copiedKey === `key-${apiKey.id}` ? 'white' : '#374151',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {copiedKey === `key-${apiKey.id}` ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <code style={{ 
                        fontSize: '11px', 
                        background: '#f9fafb', 
                        padding: '4px 8px', 
                        borderRadius: '4px',
                        display: 'block',
                        wordBreak: 'break-all'
                      }}>
                        {apiKey.key}
                      </code>
                    </div>

                    {apiKey.webhook_url && (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <strong style={{ fontSize: '13px' }}>Webhook URL:</strong>
                          <button
                            onClick={() => copyToClipboard(apiKey.webhook_url, `webhook-${apiKey.id}`)}
                            style={{ 
                              padding: '4px 8px', 
                              fontSize: '11px', 
                              background: copiedKey === `webhook-${apiKey.id}` ? '#10b981' : '#f3f4f6',
                              color: copiedKey === `webhook-${apiKey.id}` ? 'white' : '#374151',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            {copiedKey === `webhook-${apiKey.id}` ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                        <code style={{ 
                          fontSize: '11px', 
                          background: '#f9fafb', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          display: 'block',
                          wordBreak: 'break-all'
                        }}>
                          {apiKey.webhook_url}
                        </code>
                      </div>
                    )}
                    
                    <p className="body-small" style={{ marginBottom: '4px' }}>
                      <strong>Permissions:</strong> {apiKey.permissions.join(', ')}
                    </p>
                    
                    {apiKey.last_used && (
                      <p className="body-small">
                        <strong>Last Used:</strong> {new Date(apiKey.last_used).toLocaleString()}
                      </p>
                    )}
                  </div>
                  
                  <div className="integration-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => handleRegenerateApiKey(apiKey.id)}
                    >
                      <RefreshCw size={16} />
                      Regenerate
                    </button>
                    <button
                      className="btn-danger"
                      onClick={() => handleDeleteApiKey(apiKey.id)}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingIntegration ? 'Edit Integration' : 'Add Integration'}</h2>
              <button onClick={() => setShowModal(false)} className="modal-close">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Integration Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Google Ads Integration"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value, provider: '' })}
                  required
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Provider *</label>
                <select
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  required
                >
                  <option value="">Select Provider</option>
                  {PROVIDERS[formData.category]?.map(provider => (
                    <option key={provider} value={provider}>{provider}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>API Key *</label>
                <input
                  type="password"
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  placeholder="Enter API key"
                  required
                />
              </div>

              <div className="form-group">
                <label>Base URL</label>
                <input
                  type="url"
                  value={formData.base_url}
                  onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                  placeholder="https://api.example.com"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingIntegration ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Missed Call Numbers Tab */}
      {activeTab === 'missed-call-numbers' && (
        <div>
          {/* Webhook URL Info Banner */}
          <div style={{ 
            background: '#eff6ff', 
            border: '1px solid #bfdbfe', 
            borderRadius: '8px', 
            padding: '16px', 
            marginBottom: '24px' 
          }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
              📞 Missed Call Webhook URL
            </h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <code style={{ 
                flex: 1,
                fontSize: '13px', 
                background: 'white', 
                padding: '8px 12px', 
                borderRadius: '4px',
                border: '1px solid #bfdbfe',
                wordBreak: 'break-all'
              }}>
                {BACKEND_URL}/api/leads/webhook/missed-call
              </code>
              <button
                onClick={() => copyToClipboard(`${BACKEND_URL}/api/leads/webhook/missed-call`, 'webhook-url')}
                style={{ 
                  padding: '8px 16px', 
                  fontSize: '13px', 
                  background: copiedKey === 'webhook-url' ? '#10b981' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {copiedKey === 'webhook-url' ? '✓ Copied!' : 'Copy URL'}
              </button>
            </div>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#1e40af' }}>
              Configure this webhook URL in your missed call service (Exotel, Knowlarity, etc.) to automatically create leads.
            </p>
          </div>

          {/* Payload Documentation */}
          <div style={{ 
            background: '#fef3c7', 
            border: '1px solid #fbbf24', 
            borderRadius: '8px', 
            padding: '16px', 
            marginBottom: '24px' 
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
              📋 Expected Webhook Payload
            </h4>
            <div style={{ fontSize: '13px', color: '#78350f', lineHeight: '1.6' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Required Fields:</p>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
                <li><code>caller_phone</code> or <code>CallFrom</code> or <code>cli</code> - Caller's phone number</li>
                <li><code>called_number</code> or <code>CallTo</code> or <code>did</code> - Your missed call number</li>
              </ul>
              
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Optional Fields:</p>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
                <li><code>caller_name</code> or <code>name</code> - Caller's name (defaults to "Unknown Caller")</li>
                <li><code>timestamp</code> or <code>call_time</code> - Call timestamp</li>
              </ul>

              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Example JSON Payload:</p>
              <pre style={{ 
                background: '#fff', 
                padding: '12px', 
                borderRadius: '4px', 
                border: '1px solid #fbbf24',
                fontSize: '12px',
                overflow: 'auto',
                margin: '0'
              }}>
{`{
  "caller_phone": "+919876543210",
  "called_number": "18001234567",
  "caller_name": "John Doe",
  "timestamp": "2024-11-14T10:30:00Z"
}`}
              </pre>
              
              <p style={{ margin: '12px 0 0 0', fontSize: '12px' }}>
                💡 <strong>Tip:</strong> The system automatically detects the lead source based on the called number configured below. Supports multiple payload formats from different providers.
              </p>
            </div>
          </div>

          {loading ? (
            <p>Loading missed call numbers...</p>
          ) : missedCallNumbers.length === 0 ? (
            <div className="empty-state">
              <p>No missed call numbers configured yet. Add your first number to start tracking missed calls.</p>
            </div>
          ) : (
            <div className="integrations-grid">
              {missedCallNumbers.map(number => (
                <div key={number.id} className="integration-card">
                  <div className="integration-header">
                    <div>
                      <h3 className="heading-4">{number.number}</h3>
                      <p className="caption">{number.platform} • Source: {number.source_name}</p>
                    </div>
                    <span style={{
                      padding: '4px 12px',
                      background: number.enabled ? '#d1fae5' : '#fee2e2',
                      color: number.enabled ? '#065f46' : '#991b1b',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {number.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="integration-details">
                    {number.description && (
                      <p className="body-small" style={{ marginBottom: '8px' }}>
                        <strong>Description:</strong> {number.description}
                      </p>
                    )}
                    
                    <p className="body-small" style={{ marginBottom: '4px' }}>
                      <strong>Total Calls Received:</strong> {number.total_calls_received}
                    </p>
                    
                    {number.last_call_received && (
                      <p className="body-small" style={{ marginBottom: '4px' }}>
                        <strong>Last Call:</strong> {new Date(number.last_call_received).toLocaleString()}
                      </p>
                    )}

                    <p className="body-small" style={{ marginBottom: '4px' }}>
                      <strong>Created:</strong> {new Date(number.created_at).toLocaleDateString()} by {number.created_by}
                    </p>
                  </div>
                  
                  <div className="integration-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => handleEditMissedCallNumber(number)}
                    >
                      <Edit2 size={16} />
                      Edit
                    </button>
                    <button
                      className="btn-danger"
                      onClick={() => handleDeleteMissedCallNumber(number.id)}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* API Key Creation Modal */}
      {showApiKeyModal && (
        <div className="modal-overlay" onClick={() => setShowApiKeyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="heading-3">Generate API Key</h2>
              <button onClick={() => setShowApiKeyModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleCreateApiKey}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={apiKeyFormData.name}
                  onChange={(e) => setApiKeyFormData({ ...apiKeyFormData, name: e.target.value })}
                  placeholder="e.g., Google Ads Webhook, Third-Party Integration"
                  required
                />
              </div>

              <div className="form-group">
                <label>Permissions *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {AVAILABLE_PERMISSIONS.map(permission => (
                    <label key={permission} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={apiKeyFormData.permissions.includes(permission)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setApiKeyFormData({
                              ...apiKeyFormData,
                              permissions: [...apiKeyFormData.permissions, permission]
                            });
                          } else {
                            setApiKeyFormData({
                              ...apiKeyFormData,
                              permissions: apiKeyFormData.permissions.filter(p => p !== permission)
                            });
                          }
                        }}
                      />
                      <span style={{ fontSize: '14px' }}>{permission}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>IP Whitelist (Optional)</label>
                <input
                  type="text"
                  value={apiKeyFormData.ip_whitelist.join(', ')}
                  onChange={(e) => setApiKeyFormData({ 
                    ...apiKeyFormData, 
                    ip_whitelist: e.target.value.split(',').map(ip => ip.trim()).filter(ip => ip)
                  })}
                  placeholder="e.g., 192.168.1.1, 10.0.0.1 (comma separated)"
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Leave empty to allow all IPs
                </small>
              </div>

              <div className="form-group">
                <label>Default Lead Source</label>
                <select
                  value={apiKeyFormData.default_source}
                  onChange={(e) => setApiKeyFormData({ ...apiKeyFormData, default_source: e.target.value })}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '100%' }}
                >
                  <option value="">Select source (or send in payload)</option>
                  {sources.map((source) => (
                    <option key={source.id} value={source.name}>
                      {source.name}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Select a default source or leave empty to require it in webhook payload
                </small>
              </div>

              <div className="modal-actions" style={{ display: 'flex', gap: '12px' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowApiKeyModal(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  Generate API Key
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tracking Pixels Tab */}
      {activeTab === 'tracking-pixels' && (
        <TrackingScriptsManager />
      )}

      {/* Missed Call Number Modal */}
      {showMissedCallModal && (
        <div className="modal-overlay" onClick={() => setShowMissedCallModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                {editingMissedCallNumber ? 'Edit Missed Call Number' : 'Add Missed Call Number'}
              </h3>
              <button onClick={() => {
                setShowMissedCallModal(false);
                setEditingMissedCallNumber(null);
              }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreateMissedCallNumber}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Phone Number *</label>
                <input
                  type="text"
                  value={missedCallFormData.number}
                  onChange={(e) => setMissedCallFormData(prev => ({ ...prev, number: e.target.value }))}
                  placeholder="e.g., 1800-123-4567, 08068264080"
                  style={{ width: '100%' }}
                  required
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Enter the toll-free or missed call number exactly as it appears in webhooks
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Source Name *</label>
                <select
                  value={missedCallFormData.source_name}
                  onChange={(e) => setMissedCallFormData(prev => ({ ...prev, source_name: e.target.value }))}
                  style={{ width: '100%' }}
                  required
                >
                  {SOURCE_OPTIONS.map(source => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Leads from this number will be tagged with this source
                </small>
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Platform *</label>
                <select
                  value={missedCallFormData.platform}
                  onChange={(e) => setMissedCallFormData(prev => ({ ...prev, platform: e.target.value }))}
                  style={{ width: '100%' }}
                  required
                >
                  {MISSED_CALL_PLATFORMS.map(platform => (
                    <option key={platform} value={platform}>{platform}</option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  The missed call service provider
                </small>
              </div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>Description (Optional)</label>
                <textarea
                  value={missedCallFormData.description}
                  onChange={(e) => setMissedCallFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  placeholder="e.g., Google Ads Campaign - Search Network"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowMissedCallModal(false);
                    setEditingMissedCallNumber(null);
                  }}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ flex: 1 }}
                >
                  {editingMissedCallNumber ? 'Update' : 'Add Number'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
